// src/pages/Home.jsx
import React, {
  useEffect, useState, useCallback, useRef, useMemo,
} from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../firebase';
import {
  collection, query, orderBy, limit, startAfter, onSnapshot,
  getDocs, doc, Timestamp,
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import StoriesBar from '../components/home/StoriesBar';
import PostComposer from '../components/home/PostComposer';
import PostCard from '../components/home/PostCard';
import { normalizeMedia } from '../utils/feedHelpers';

/* ================================================================== */
/* Constants                                                           */
/* ================================================================== */

const POSTS_PAGE_SIZE = 30;
const STORY_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const RECENT_MEDIA_LIMIT = 8;
const RECENT_MEDIA_PER_POST = 2;

const PULL_THRESHOLD = 70;       // px to trigger refresh
const PULL_MAX = 120;            // max visual pull distance
const PULL_RESISTANCE = 0.5;     // rubber-band factor

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=2000&q=80';

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

const toMs = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const t = Date.parse(value);
    return Number.isNaN(t) ? 0 : t;
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') {
    const d = value.toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  }
  return 0;
};

const getPostTime = (p) => toMs(p?.postDate) || toMs(p?.createdAt) || 0;

const getStoryExpiry = (s) => {
  const explicit = toMs(s?.expiresAt);
  if (explicit > 0) return explicit;
  const created = toMs(s?.createdAt);
  if (created > 0) return created + STORY_MAX_AGE_MS;
  return 0;
};

/* ================================================================== */
/* Post visibility rule                                                */
/* ================================================================== */

/**
 * Can `viewer` see `post`?
 *
 *  - Guest (no user):   only Public + approved
 *  - Admin:             everything
 *  - Author:            everything they wrote
 *  - Approved member:   Public + Private (members-only) + own
 *                       Excludes 'onlyMe' posts by others
 *
 * @param {object|null} post
 * @param {object|null} viewer   // { uid, ... } or null for guests
 * @param {boolean} isAdminViewer
 */
const canViewPost = (post, viewer, isAdminViewer = false) => {
  if (!post) return false;

  // Guests see only public + approved
  if (!viewer) {
    return String(post.visibility || '').toLowerCase() === 'public'
      && post.approved === true;
  }

  // Admins see everything
  if (isAdminViewer) return true;

  // Author always sees their own post
  const isAuthor =
    post.authorId === viewer.uid ||
    post.userId === viewer.uid ||
    post.uid === viewer.uid ||
    post.author?.id === viewer.uid;
  if (isAuthor) return true;

  // Rejected posts hidden from non-admins
  if (post.approved === false) return false;

  const vis = String(post.visibility || 'public').toLowerCase();

  // 'onlyMe' = truly private, invisible to other members
  if (vis === 'onlyme' || vis === 'only_me') return false;

  // Public OR Private (members-only) → both visible to approved members
  if (vis === 'public' || vis === 'private') return true;

  // Unknown visibility: default to hide (safer)
  return false;
};

/* ================================================================== */
/* Feature card (guest hero)                                           */
/* ================================================================== */

const Feature = ({ icon, title, desc }) => (
  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4 text-center">
    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-orange/20 flex items-center justify-center">
      <i className={`fas ${icon} text-orange text-xl`} aria-hidden="true" />
    </div>
    <h3 className="text-white font-semibold mb-1 text-sm">{title}</h3>
    <p className="text-white/70 text-xs">{desc}</p>
  </div>
);

/* ================================================================== */
/* Pull-to-Refresh indicator                                           */
/* ================================================================== */

const PullToRefreshIndicator = ({ pullDistance, refreshing }) => {
  const progress = Math.min(pullDistance / PULL_THRESHOLD, 1);
  const ready = pullDistance >= PULL_THRESHOLD;
  const visible = pullDistance > 0 || refreshing;

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-40 flex items-end justify-center pointer-events-none"
      style={{ height: `${Math.max(pullDistance, refreshing ? 56 : 0)}px` }}
      aria-hidden="true"
    >
      <div
        className={`mb-2 w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-colors ${
          refreshing
            ? 'bg-orange text-white'
            : ready
            ? 'bg-orange text-white'
            : 'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300'
        }`}
        style={{
          opacity: refreshing ? 1 : Math.max(progress, 0.2),
          transform: `scale(${refreshing ? 1 : 0.85 + progress * 0.15})`,
        }}
      >
        {refreshing ? (
          <i className="fas fa-spinner fa-spin text-sm" />
        ) : (
          <i
            className="fas fa-arrow-down text-sm transition-transform"
            style={{ transform: `rotate(${ready ? 180 : 0}deg)` }}
          />
        )}
      </div>
    </div>
  );
};

/* ================================================================== */
/* Media Lightbox                                                      */
/* ================================================================== */

const MediaLightbox = ({ media, initialIndex = 0, onClose }) => {
  const [index, setIndex] = useState(initialIndex);
  const handlersRef = useRef({ goPrev: null, goNext: null });

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + media.length) % media.length);
  }, [media.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % media.length);
  }, [media.length]);

  handlersRef.current = { goPrev, goNext };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlersRef.current.goPrev?.();
      if (e.key === 'ArrowRight') handlersRef.current.goNext?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (index >= media.length) setIndex(0);
  }, [media.length, index]);

  useEffect(() => {
    if (!media.length) return;
    const targets = [index + 1, index - 1]
      .filter((i) => i >= 0 && i < media.length)
      .map((i) => media[i])
      .filter((m) => m?.type === 'image' && m.url);
    targets.forEach((m) => {
      const img = new Image();
      img.src = m.url;
    });
  }, [media, index]);

  if (!media || !media.length) return null;
  const current = media[index];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label="Media viewer"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center z-30 transition"
        >
          <i className="fas fa-times text-lg" aria-hidden="true" />
        </button>

        {media.length > 1 && (
          <div className="absolute top-4 left-4 bg-black/60 text-white text-sm rounded-full px-3 py-1 z-30">
            {index + 1} / {media.length}
          </div>
        )}

        {current.isCover && media.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-orange text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg z-30">
            <i className="fas fa-star text-[10px]" aria-hidden="true" />
            Cover
          </div>
        )}

        {media.length > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous"
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-30 transition"
          >
            <i className="fas fa-chevron-left text-xl" aria-hidden="true" />
          </button>
        )}

        {media.length > 1 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next"
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-30 transition"
          >
            <i className="fas fa-chevron-right text-xl" aria-hidden="true" />
          </button>
        )}

        <div
          className="max-w-full max-h-full w-full h-full flex items-center justify-center p-4"
          onClick={(e) => e.stopPropagation()}
        >
          {current.type === 'video' ? (
            <video
              key={current.url}
              src={current.url}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
          ) : (
            <img
              key={current.url}
              src={current.url}
              alt=""
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
              draggable={false}
            />
          )}
        </div>

        {media.length > 1 && media.length <= 12 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-30">
            {media.map((m, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex(i);
                }}
                aria-label={`Go to item ${i + 1}`}
                className={`w-2 h-2 rounded-full transition-all ${
                  i === index
                    ? 'bg-white scale-125'
                    : 'bg-white/40 hover:bg-white/60'
                } ${m.type === 'video' ? 'ring-1 ring-white/70' : ''}`}
              />
            ))}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

/* ================================================================== */
/* Recent Media Strip                                                  */
/* ================================================================== */

const RecentMediaStrip = ({ items, onOpenItem }) => {
  if (!items.length) return null;

  return (
    <section
      className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 mb-4 border border-gray-100 dark:border-gray-700/60"
      aria-label="Recent media"
    >
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          <i className="fas fa-images text-pink-500" aria-hidden="true" />
          <h2 className="text-sm font-semibold text-gray-800 dark:text-white">
            Recent Media
          </h2>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            · {items.length}
          </span>
        </div>
        <Link
          to="/media"
          className="text-xs font-medium text-orange hover:text-orange-dark flex items-center gap-1"
        >
          View all
          <i className="fas fa-arrow-right text-[10px]" aria-hidden="true" />
        </Link>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-thin">
        {items.map((item, i) => (
          <button
            key={`${item.postId}-${item.url}-${i}`}
            type="button"
            onClick={() => onOpenItem(i)}
            className="relative w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-200 dark:bg-gray-700 group focus:outline-none focus:ring-2 focus:ring-orange focus:ring-inset"
            aria-label={`View ${
              item.type === 'video' ? 'video' : 'photo'
            }${item.postAuthor ? ` by ${item.postAuthor}` : ''}`}
          >
            {item.type === 'video' ? (
              <>
                <video
                  src={item.url}
                  className="absolute inset-0 w-full h-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-7 h-7 rounded-full bg-black/60 flex items-center justify-center">
                    <i
                      className="fas fa-play text-white text-[10px]"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </>
            ) : (
              <img
                src={item.url}
                alt=""
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            )}

            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

            {item.isCover && (
              <div className="absolute top-1 left-1 bg-orange text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow">
                <i className="fas fa-star text-[6px]" aria-hidden="true" />
                Cover
              </div>
            )}

            {item.type === 'video' && (
              <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center">
                <i className="fas fa-video text-[7px]" aria-hidden="true" />
              </div>
            )}

            {item.postAuthor && (
              <p className="absolute bottom-1 left-1 right-1 text-[9px] text-white font-medium truncate opacity-0 group-hover:opacity-100 transition-opacity drop-shadow">
                {item.postAuthor}
              </p>
            )}
          </button>
        ))}
      </div>
    </section>
  );
};

/* ================================================================== */
/* New Posts Pill                                                      */
/* ================================================================== */

const NewPostsPill = ({ count, onClick }) => {
  if (!count || count <= 0) return null;
  return (
    <motion.button
      type="button"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      onClick={onClick}
      className="fixed top-24 left-1/2 -translate-x-1/2 z-50 bg-orange hover:bg-orange-dark text-white text-sm font-semibold px-4 py-2 rounded-full shadow-lg flex items-center gap-2 transition-colors"
    >
      <i className="fas fa-arrow-up" aria-hidden="true" />
      {count} new {count === 1 ? 'post' : 'posts'}
    </motion.button>
  );
};

/* ================================================================== */
/* Feed Skeleton                                                       */
/* ================================================================== */

const FeedSkeleton = ({ count = 3 }) => (
  <div className="space-y-4">
    {Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-100 dark:border-gray-700/60 animate-pulse"
      >
        <div className="flex gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
            <div className="h-2 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
        <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/* Pull-to-Refresh hook                                                */
/* ================================================================== */

/**
 * Native-feeling pull-to-refresh for touch devices.
 * Only activates when the page is scrolled to the very top
 * and the user drags downward.
 */
const usePullToRefresh = ({ onRefresh, enabled = true }) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const startYRef = useRef(null);
  const pullingRef = useRef(false);
  const refreshingRef = useRef(false);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return undefined;

    const reset = () => {
      startYRef.current = null;
      pullingRef.current = false;
      setPullDistance(0);
    };

    const handleTouchStart = (e) => {
      if (refreshingRef.current) return;
      if (window.scrollY > 0) return;
      if (e.touches.length !== 1) return;
      startYRef.current = e.touches[0].clientY;
      pullingRef.current = false;
    };

    const handleTouchMove = (e) => {
      if (startYRef.current == null || refreshingRef.current) return;
      if (window.scrollY > 0) {
        reset();
        return;
      }
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        setPullDistance(0);
        return;
      }
      pullingRef.current = true;
      const dampened = Math.min(delta * PULL_RESISTANCE, PULL_MAX);
      setPullDistance(dampened);
      // Prevent native scroll/bounce while actively pulling
      if (e.cancelable) e.preventDefault();
    };

    const handleTouchEnd = async () => {
      if (refreshingRef.current) {
        reset();
        return;
      }
      const shouldRefresh =
        pullingRef.current && pullDistance >= PULL_THRESHOLD;

      if (shouldRefresh) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullDistance(56);
        try {
          await onRefreshRef.current?.();
        } finally {
          // Small delay so the spinner is visible even on fast refreshes
          setTimeout(() => {
            refreshingRef.current = false;
            setRefreshing(false);
            reset();
          }, 350);
        }
      } else {
        reset();
      }
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
    // pullDistance intentionally included so touchend reads latest value
  }, [enabled, pullDistance]);

  return { pullDistance, refreshing };
};

/* ================================================================== */
/* Home                                                                */
/* ================================================================== */

const Home = () => {
  const { user, isAdmin, isApproved, loading: authLoading } = useAuth();

  const [stories, setStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [postsError, setPostsError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);

  const [liveUserDoc, setLiveUserDoc] = useState(null);

  const [newPostsCount, setNewPostsCount] = useState(0);
  const knownTopIdRef = useRef(null);
  const isScrolledDownRef = useRef(false);

  const [lightbox, setLightbox] = useState(null);

  /* ================================================================ */
  /* Live user doc (approval / block changes)                         */
  /* ================================================================ */
  useEffect(() => {
    if (!user?.uid) {
      setLiveUserDoc(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        setLiveUserDoc(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      (err) => console.warn('user doc listener:', err),
    );
    return () => unsub();
  }, [user?.uid]);

  const effectiveApproved = useMemo(() => {
    if (isApproved === true) return true;
    const d = liveUserDoc;
    return (
      d?.approved === true ||
      d?.isApproved === true ||
      d?.registrationStatus === 'approved'
    );
  }, [isApproved, liveUserDoc]);

  /* ================================================================ */
  /* Live stories                                                     */
  /* ================================================================ */
  useEffect(() => {
    if (!user || !effectiveApproved) {
      setStories([]);
      setStoriesLoading(false);
      return undefined;
    }

    const q = query(
      collection(db, 'stories'),
      orderBy('createdAt', 'desc'),
      limit(100),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const now = Date.now();
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const active = all.filter((s) => getStoryExpiry(s) > now);
        const visible = active.filter((s) => {
          if (isAdmin) return true;
          if (s.authorId === user.uid) return true;
          return s.approved !== false;
        });
        setStories(visible);
        setStoriesLoading(false);
      },
      (err) => {
        console.warn('stories listener:', err);
        setStoriesLoading(false);
      },
    );

    let tick = null;
    const startTick = () => {
      if (tick) return;
      tick = setInterval(() => {
        setStories((prev) => prev.filter((s) => getStoryExpiry(s) > Date.now()));
      }, 60 * 1000);
    };
    const stopTick = () => {
      if (tick) {
        clearInterval(tick);
        tick = null;
      }
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        setStories((prev) => prev.filter((s) => getStoryExpiry(s) > Date.now()));
        startTick();
      } else {
        stopTick();
      }
    };

    if (document.visibilityState === 'visible') startTick();
    document.addEventListener('visibilitychange', onVis);

    return () => {
      unsub();
      stopTick();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, effectiveApproved, isAdmin]);

  /* ================================================================ */
  /* Live posts (first page)                                          */
  /* ================================================================ */
  useEffect(() => {
    setPostsLoading(true);
    setPostsError('');

    const q = query(
      collection(db, 'posts'),
      orderBy('createdAt', 'desc'),
      limit(POSTS_PAGE_SIZE),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // ✅ Option B: use shared visibility helper
        const visible = all.filter((p) => canViewPost(p, user, isAdmin));

        visible.sort((a, b) => getPostTime(b) - getPostTime(a));

        const sliced = visible.slice(0, POSTS_PAGE_SIZE);
        setPosts(sliced);
        setLastDoc(snap.docs[snap.docs.length - 1] || null);
        setHasMore(snap.size === POSTS_PAGE_SIZE);
        setPostsLoading(false);

        const topId = sliced[0]?.id || null;
        if (knownTopIdRef.current && topId && topId !== knownTopIdRef.current) {
          if (isScrolledDownRef.current) {
            setNewPostsCount((c) => c + 1);
          }
        }
        knownTopIdRef.current = topId;
      },
      (err) => {
        console.warn('posts listener:', err);
        setPostsError('Failed to load posts.');
        setPostsLoading(false);
      },
    );

    return () => unsub();
  }, [user?.uid, isAdmin]);

  /* ---------------- Scroll tracking ---------------- */
  useEffect(() => {
    const onScroll = () => {
      isScrolledDownRef.current = window.scrollY > 400;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ---------------- Load more ---------------- */
  const loadMore = useCallback(async () => {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const q = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        startAfter(lastDoc),
        limit(POSTS_PAGE_SIZE),
      );
      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // ✅ Option B: use shared visibility helper
      const visible = all.filter((p) => canViewPost(p, user, isAdmin));
      visible.sort((a, b) => getPostTime(b) - getPostTime(a));

      setPosts((prev) => {
        const existingIds = new Set(prev.map((p) => p.id));
        return [...prev, ...visible.filter((p) => !existingIds.has(p.id))];
      });
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.size === POSTS_PAGE_SIZE);
    } catch (err) {
      console.warn('loadMore failed:', err);
      setPostsError('Failed to load more posts.');
    } finally {
      setLoadingMore(false);
    }
  }, [lastDoc, loadingMore, user, isAdmin]);

  /* ---------------- Handle new-posts pill click ---------------- */
  const revealNewPosts = useCallback(() => {
    setNewPostsCount(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  /* ---------------- Post mutation callbacks ---------------- */
  const handlePostDeleted = useCallback((postId) => {
    setPosts((prev) => prev.filter((p) => p.id !== postId));
  }, []);

  const handlePostUpdated = useCallback((postId, updates) => {
    setPosts((prev) => {
      const now = Timestamp.now();
      const sanitized = { ...updates };
      if (sanitized.updatedAt && typeof sanitized.updatedAt === 'object') {
        sanitized.updatedAt = now;
      }
      if (sanitized.editedAt && typeof sanitized.editedAt === 'object') {
        sanitized.editedAt = now;
      }
      const next = prev.map((p) =>
        p.id === postId ? { ...p, ...sanitized } : p,
      );
      next.sort((a, b) => getPostTime(b) - getPostTime(a));
      return next;
    });
  }, []);

  /* ================================================================ */
  /* Pull-to-refresh                                                  */
  /* ================================================================ */
  const handleRefresh = useCallback(async () => {
    try {
      const q = query(
        collection(db, 'posts'),
        orderBy('createdAt', 'desc'),
        limit(POSTS_PAGE_SIZE),
      );
      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const visible = all.filter((p) => canViewPost(p, user, isAdmin));
      visible.sort((a, b) => getPostTime(b) - getPostTime(a));

      setPosts(visible.slice(0, POSTS_PAGE_SIZE));
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.size === POSTS_PAGE_SIZE);
      setNewPostsCount(0);
      knownTopIdRef.current = visible[0]?.id || null;

      if (user && effectiveApproved) {
        const sq = query(
          collection(db, 'stories'),
          orderBy('createdAt', 'desc'),
          limit(100),
        );
        const ss = await getDocs(sq);
        const now = Date.now();
        const active = ss.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((s) => getStoryExpiry(s) > now)
          .filter((s) => {
            if (isAdmin) return true;
            if (s.authorId === user.uid) return true;
            return s.approved !== false;
          });
        setStories(active);
      }
    } catch (err) {
      console.warn('pull-to-refresh failed:', err);
      setPostsError('Failed to refresh. Please try again.');
    }
  }, [user, isAdmin, effectiveApproved]);

  const showPTR = !!user && effectiveApproved;
  const { pullDistance, refreshing } = usePullToRefresh({
    onRefresh: handleRefresh,
    enabled: showPTR,
  });

  /* ================================================================ */
  /* Recent media                                                     */
  /* ================================================================ */
  const recentMedia = useMemo(() => {
    const out = [];
    for (const post of posts) {
      const media = normalizeMedia(post);
      if (!media.length) continue;
      const ts = getPostTime(post);
      const take = Math.min(RECENT_MEDIA_PER_POST, media.length);
      for (let i = 0; i < take; i++) {
        const m = media[i];
        out.push({
          url: m.url,
          type: m.type || 'image',
          isCover: !!m.isCover,
          postId: post.id,
          postAuthor: post.authorName,
          createdAt: ts,
        });
        if (out.length >= RECENT_MEDIA_LIMIT) break;
      }
      if (out.length >= RECENT_MEDIA_LIMIT) break;
    }
    return out;
  }, [posts]);

  /* ---------------- Lightbox ---------------- */
  const openLightbox = useCallback((media, initialIndex = 0) => {
    setLightbox({ media, initialIndex });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const openRecentMedia = useCallback(
    (index) => openLightbox(recentMedia, index),
    [openLightbox, recentMedia],
  );

  /* ---------------- Auth loading ---------------- */
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="w-12 h-12 border-4 border-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  /* ================================================================ */
  /* Guests                                                            */
  /* ================================================================ */
  if (!user) {
    const publicPosts = posts.filter(
      (p) => String(p.visibility || '').toLowerCase() === 'public',
    );

    return (
      <>
        <main className="min-h-screen bg-gray-100 dark:bg-gray-900">
          <h1 className="sr-only">IIEE-ERCSA Home</h1>

          {/* Hero */}
          <div className="relative min-h-[85vh] w-full overflow-hidden -mt-16">
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${HERO_IMAGE})` }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/80" />
            <div
              className="absolute top-1/4 left-10 w-72 h-72 bg-orange/30 rounded-full blur-3xl"
              aria-hidden="true"
            />
            <div
              className="absolute bottom-1/4 right-10 w-96 h-96 bg-orange/20 rounded-full blur-3xl"
              aria-hidden="true"
            />

            <div className="relative z-10 min-h-[85vh] flex flex-col items-center justify-center text-center px-4 py-20">
              <div className="mb-6 flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-5 py-2">
                <img
                  src="/images/iiee-logo.png"
                  alt="IIEE-ERCSA Logo"
                  className="h-8 w-8 object-contain"
                />
                <span className="text-white text-sm font-medium tracking-wide">
                  IIEE-ERCSA · Eastern Region Chapter
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight max-w-4xl drop-shadow-lg">
                Connect with Fellow{' '}
                <span className="text-orange">Electrical Engineers</span>
              </h1>

              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl drop-shadow-md">
                Share stories, post updates, engage with members, and stay
                connected with the IIEE-ERCSA community — all in one place.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
                <Link
                  to="/register"
                  className="group bg-orange hover:bg-orange-dark text-white px-8 py-4 rounded-full font-semibold text-base shadow-2xl hover:shadow-orange/50 transition-all transform hover:scale-105 flex items-center gap-2"
                >
                  <i className="fas fa-user-plus" aria-hidden="true" />
                  Join the Community
                </Link>
                <Link
                  to="/login"
                  className="bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-full font-semibold text-base transition-all flex items-center gap-2"
                >
                  <i className="fas fa-sign-in-alt" aria-hidden="true" />
                  Sign In
                </Link>
              </div>

              <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl w-full">
                <Feature icon="fa-globe" title="Public Feed" desc="See what we share" />
                <Feature icon="fa-heart" title="Community" desc="Join the discussion" />
                <Feature icon="fa-comments" title="Connect" desc="Chat with members" />
              </div>
            </div>
          </div>

          {/* Public feed */}
          <div className="max-w-2xl mx-auto px-4 py-10">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                  Latest Public Posts
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Shared publicly by our members
                </p>
              </div>
              <Link
                to="/register"
                className="text-xs font-medium bg-orange/10 text-orange px-3 py-1.5 rounded-full hover:bg-orange/20 transition"
              >
                Join to see more →
              </Link>
            </div>

            {postsLoading ? (
              <FeedSkeleton count={2} />
            ) : publicPosts.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-12 text-center">
                <i
                  className="fas fa-globe text-4xl text-gray-300 mb-3"
                  aria-hidden="true"
                />
                <p className="text-gray-500 dark:text-gray-400 mb-4">
                  No public posts yet.
                </p>
                <Link
                  to="/register"
                  className="inline-block bg-orange hover:bg-orange-dark text-white px-5 py-2 rounded-full text-sm font-medium transition"
                >
                  Sign up to see more
                </Link>
              </div>
            ) : (
              publicPosts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onDeleted={handlePostDeleted}
                  onUpdated={handlePostUpdated}
                  onMediaClick={openLightbox}
                  isGuest
                />
              ))
            )}
          </div>

          {/* Bottom CTA */}
          <div className="text-center pb-12 px-4">
            <div className="max-w-md mx-auto bg-gradient-to-br from-orange to-orange-dark text-white rounded-2xl p-8 shadow-xl">
              <i className="fas fa-users text-4xl mb-3" aria-hidden="true" />
              <h3 className="text-xl font-bold mb-2">Ready to Join?</h3>
              <p className="text-sm text-white/90 mb-5">
                Create your free account to post, comment, message members, and
                more.
              </p>
              <Link
                to="/register"
                className="inline-block bg-white text-orange hover:bg-gray-100 px-6 py-3 rounded-full font-semibold text-sm transition shadow-md"
              >
                Get Started →
              </Link>
            </div>
          </div>
        </main>

        {lightbox && (
          <MediaLightbox
            media={lightbox.media}
            initialIndex={lightbox.initialIndex}
            onClose={closeLightbox}
          />
        )}
      </>
    );
  }

  /* ================================================================ */
  /* Pending approval                                                  */
  /* ================================================================ */
  if (!effectiveApproved) {
    return (
      <main className="min-h-[80vh] flex items-center justify-center px-4 py-12 bg-gray-100 dark:bg-gray-900">
        <div className="text-center max-w-lg bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <i
              className="fas fa-clock text-2xl text-yellow-600"
              aria-hidden="true"
            />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Account Pending Approval
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Your account is being reviewed. You'll gain access to the feed once
            an administrator approves it.
          </p>
        </div>
      </main>
    );
  }

  /* ================================================================ */
  /* Main feed                                                         */
  /* ================================================================ */
  return (
    <>
      <PullToRefreshIndicator
        pullDistance={pullDistance}
        refreshing={refreshing}
      />

      <main
        className="min-h-screen bg-gray-100 dark:bg-gray-900 py-4 md:py-6 px-3 md:px-4"
        style={{
          transform: pullDistance ? `translateY(${pullDistance}px)` : undefined,
          transition: pullDistance ? 'none' : 'transform 0.25s ease',
        }}
      >
        <h1 className="sr-only">IIEE-ERCSA Feed</h1>

        <div className="max-w-2xl mx-auto">
          <StoriesBar stories={stories} loading={storiesLoading} />

          {/* Inline composer only — floating composer removed */}
          <PostComposer
            onPosted={() => {
              /* listener handles */
            }}
            suppressFloat
          />

          {!postsLoading && recentMedia.length > 0 && (
            <RecentMediaStrip
              items={recentMedia}
              onOpenItem={openRecentMedia}
            />
          )}

          {postsError && (
            <div
              role="alert"
              className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 text-xs rounded-lg p-3 flex items-start gap-2"
            >
              <i
                className="fas fa-exclamation-circle mt-0.5"
                aria-hidden="true"
              />
              <span className="flex-1">{postsError}</span>
              <button
                type="button"
                onClick={() => setPostsError('')}
                aria-label="Dismiss"
                className="text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
          )}

          {postsLoading ? (
            <FeedSkeleton count={3} />
          ) : posts.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-12 text-center">
              <i
                className="fas fa-newspaper text-4xl text-gray-300 mb-3"
                aria-hidden="true"
              />
              <p className="text-gray-500 dark:text-gray-400">
                No posts yet. Be the first to share something!
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <PostCard
                  key={post.id}
                  post={post}
                  onDeleted={handlePostDeleted}
                  onUpdated={handlePostUpdated}
                  onMediaClick={openLightbox}
                />
              ))}
            </div>
          )}

          {hasMore && !postsLoading && (
            <div className="text-center pt-4 pb-6">
              <button
                type="button"
                onClick={loadMore}
                disabled={loadingMore}
                className="px-5 py-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 inline-flex items-center gap-2 transition shadow-sm"
              >
                {loadingMore ? (
                  <>
                    <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                    Loading…
                  </>
                ) : (
                  <>
                    <i className="fas fa-arrow-down" aria-hidden="true" />
                    Load older posts
                  </>
                )}
              </button>
            </div>
          )}

          {!hasMore && posts.length >= POSTS_PAGE_SIZE && (
            <p className="text-center text-xs text-gray-500 py-4">
              <i className="fas fa-check-circle mr-1" aria-hidden="true" />
              You've reached the end.
            </p>
          )}
        </div>
      </main>

      <AnimatePresence>
        {newPostsCount > 0 && (
          <NewPostsPill count={newPostsCount} onClick={revealNewPosts} />
        )}
      </AnimatePresence>

      {lightbox && (
        <MediaLightbox
          media={lightbox.media}
          initialIndex={lightbox.initialIndex}
          onClose={closeLightbox}
        />
      )}
    </>
  );
};

export default Home;